import * as R from 'ramda';

import { LAB_PAGE_SIZE, PATH } from '../constants';
import { navigate } from '../submodules/shared/utils/navigator';
import {
    getLabDetails,
    getLabItems,
    postLabDetails,
    deleteLabDetails,
    getLabInstructions,
    postLabKeys,
    getProducts,
    getLabsByProduct,
} from '../utils/api';
import { dateOr } from '../utils/format-date';
import { notifyTimeout } from '../utils/notify-timeout';

import ACTION_TYPES from './action-types';

export const upsertLab = (payload) => {
    payload = R.mergeRight(payload, {
        live_event_start: dateOr(payload.live_event_start),
        live_event_end: dateOr(payload.live_event_end),
    });

    return (dispatch) =>
        postLabDetails(payload).then(
            ({ result, data }) => {
                if (result === 'success') {
                    //Clear the lab items so home will fetch new results when it loads
                    dispatch({
                        type: ACTION_TYPES.UPDATE_LAB_RESULTS,
                        payload: [],
                    });
                    navigate(PATH.home);
                } else {
                    //If validation doesn't pass...
                    R.forEachObjIndexed(
                        (value, key) =>
                            dispatch({
                                type: ACTION_TYPES.SHOW_NOTIFICATION_MESSAGE,
                                payload: { type: 'danger', message: `${key}: ${value}` },
                            }),
                        data,
                    );
                    globalThis.window.scrollTo(0, 0); //If we like doing this it should be moved into the action
                }
            },
            (err) => notifyTimeout(dispatch, err),
        );
};

export const deleteLab = (id) => (dispatch) =>
    deleteLabDetails(id).then(
        ({ result }) => {
            if (result === 'success') {
                dispatch({
                    type: ACTION_TYPES.UPDATE_LAB_RESULTS,
                    payload: [],
                });
                dispatch({
                    type: ACTION_TYPES.SHOW_NOTIFICATION_MESSAGE,
                    payload: { type: 'success', message: 'Lab deleted successfully' },
                });
                navigate(PATH.home);
            } else {
                dispatch({
                    type: ACTION_TYPES.SHOW_NOTIFICATION_MESSAGE,
                    payload: { type: 'danger', message: 'Failed to delete lab' },
                });
            }
        },
        (err) => {
            if (notifyTimeout(dispatch, err)) {
                return;
            }

            dispatch({
                type: ACTION_TYPES.SHOW_NOTIFICATION_MESSAGE,
                payload: { type: 'danger', message: 'Failed to delete lab' },
            });
        },
    );

//Monotonic token for lab list fetches: only the newest request may dispatch.
//Without it a slow Load More that resolves after a sort/filter refetch would
//append rows from the old sort/filter onto the new list and overwrite the
//paging state with stale values.
let labItemsRequestSeq = 0;

export const fetchLabItems = (payload) => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.SET_LAB_FILTER,
        payload: '',
    });

    const limit = R.propOr(LAB_PAGE_SIZE, 'limit', payload);
    const offset = R.propOr(0, 'offset', payload);
    const isAppend = offset > 0;
    const requestSeq = (labItemsRequestSeq += 1);

    return getLabItems(R.mergeRight(payload, { limit, offset })).then(
        ({ data }) => {
            if (requestSeq !== labItemsRequestSeq) {
                return;
            }

            dispatch({
                type: isAppend ? ACTION_TYPES.APPEND_LAB_RESULTS : ACTION_TYPES.UPDATE_LAB_RESULTS,
                payload: data,
            });

            dispatch({
                type: ACTION_TYPES.SET_LAB_PAGINATION,
                payload: {
                    orderBy: payload.orderBy,
                    dir: payload.dir,
                    limit,
                    offset,
                    hasMore: data.length === limit,
                },
            });
        },
        (err) => {
            if (requestSeq !== labItemsRequestSeq) {
                return;
            }

            if (notifyTimeout(dispatch, err)) {
                return;
            }

            //A failed append must not wipe the already-loaded pages or corrupt paging state
            if (!isAppend) {
                dispatch({
                    type: ACTION_TYPES.UPDATE_LAB_RESULTS,
                    payload: [],
                });
            }
        },
    );
};

export const fetchLabDetails = (id) => (dispatch) =>
    getLabDetails(id).then(
        ({ data }) => {
            dispatch({
                type: ACTION_TYPES.SET_LAB_FILTER,
                payload: id,
            });

            dispatch({
                type: ACTION_TYPES.SET_LAB_DETAILS,
                //payload: data, //original
                payload: R.head(data), //fix for express.js //we return an arary because labs endpoint can contain multiple
            });
        },
        (err) => {
            if (notifyTimeout(dispatch, err)) {
                return;
            }

            dispatch({
                type: ACTION_TYPES.SET_LAB_DETAILS_FAILED,
                payload: err,
            });
        },
    );

export const clearLabDetails = () => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.CLEAR_LAB_DETAILS,
    });
    navigate(PATH.lab);
};

export const fetchLabInstructions = (id) => (dispatch) =>
    getLabInstructions(id).then(
        ({ data }) => {
            dispatch({
                type: ACTION_TYPES.SET_LAB_INSTRUCTIONS,
                payload: data,
            });
        },
        (err) => {
            if (notifyTimeout(dispatch, err)) {
                return;
            }

            dispatch({
                type: ACTION_TYPES.SHOW_NOTIFICATION_MESSAGE,
                payload: { type: 'danger', message: 'Could not find instructions' },
            });
        },
    );

export const uploadLabKeys = (payload) => (dispatch) =>
    postLabKeys(payload).then(
        () => {
            dispatch({
                type: ACTION_TYPES.CERTS_UPLOADED,
            });
            navigate(PATH.labAction(payload.id, PATH.lab_actions.VIEW));
        },
        (err) => notifyTimeout(dispatch, err),
    );

export const fetchProducts = () => (dispatch) =>
    getProducts().then(
        ({ data }) =>
            dispatch({
                type: ACTION_TYPES.SET_PRODUCTS,
                payload: data,
            }),
        (err) => notifyTimeout(dispatch, err),
    );

export const findLabsByProduct =
    ({ target }) =>
    (dispatch) =>
        getLabsByProduct(target.dataset).then(
            ({ data }) => {
                dispatch({
                    type: ACTION_TYPES.SET_EVENT_LABS,
                    payload: {
                        product: target.dataset.product,
                        data,
                    },
                });
            },
            (err) => notifyTimeout(dispatch, err),
        );
